import { BankTransferDetails } from '@/components/children/bankTransferDetails/scripts';
import { Address, BankAccount, Logo, Person } from './types'
import { isPlainObject } from './HelperFunctions';

// following proper safe guard https://dev.to/noriste/keeping-typescript-type-guards-safe-and-up-to-date-a-simpler-solution-ja3

// guard for person
export function isPerson(value: unknown): value is Person {
  if (!isPlainObject(value)) return false;

  // Required fields
  if (!value.hasOwnProperty('id')) return false;

  // deconstruct all required props
  const {
    id,
  } = value;

  // check type of required

  if (typeof id !== 'number') return false;

  // check optionals

  let firstName: string | undefined;
  let lastName: string | undefined;
  let phoneNumber: string | undefined;
  let email: string | undefined;

  if (value.hasOwnProperty('firstName')) {
    if (typeof value.firstName !== 'string' && value.firstName !== undefined) return false;
    firstName = value.firstName;
  }

  if (value.hasOwnProperty('lastName')) {
    if (typeof value.lastName !== 'string' && value.lastName !== undefined) return false;
    lastName = value.lastName;
  }

  if (value.hasOwnProperty('phoneNumber')) {
    if (typeof value.phoneNumber !== 'string' && value.phoneNumber !== undefined) return false;
    phoneNumber = value.phoneNumber;
  }

  if (value.hasOwnProperty('email')) {
    if (typeof value.email !== 'string' && value.email !== undefined) return false;
    email = value.email;
  }

  const obj = { id, firstName, lastName, phoneNumber, email };
  const isValid: Person = obj;
  const noNewOptionalProps: Omit<Required<Person>, keyof typeof obj> = {};

  return true;
}

// guard for address
export function isAddress(value: unknown): value is Address {
  if (!isPlainObject(value)) return false;

  // Required fields

  if (!value.hasOwnProperty('id')) return false;

  // deconstruct all required props
  const {
    id,
  } = value;

  // check type of required
  if (typeof id !== 'number') return false;

  // check optionals

  let street: string | undefined;
  let town: string | undefined;
  let zipCode: string | undefined;

  if (value.hasOwnProperty('street')) {
    if (typeof value.street !== 'string' && value.street !== undefined) return false;
    street = value.street;
  }

  if (value.hasOwnProperty('town')) {
    if (typeof value.town !== 'string' && value.town !== undefined) return false;
    town = value.town;
  }

  if (value.hasOwnProperty('zipCode')) {
    if (typeof value.zipCode !== 'string' && value.zipCode !== undefined) return false;
    zipCode = value.zipCode;
  }

  const obj = { id, street, town, zipCode };
  const isValid: Address = obj;
  const noNewOptionalProps: Omit<Required<Address>, keyof typeof obj> = {};

  return true;
}

// guard for bank account
export function isBankAccount(value: unknown): value is BankAccount {
  if (!isPlainObject(value)) return false;

  // Required fields

  if (!value.hasOwnProperty('id')) return false;
  if (!value.hasOwnProperty('creditInstitute')) return false;
  if (!value.hasOwnProperty('iban')) return false;
  if (!value.hasOwnProperty('bic')) return false;
  if (!value.hasOwnProperty('accountOwnerName')) return false;

  // deconstruct all required props
  const {
    id,
    creditInstitute,
    iban,
    bic,
    accountOwnerName,
  } = value;

  // check type of required

  if (typeof id !== 'number') return false;
  if (typeof creditInstitute !== 'string') return false;
  if (typeof iban !== 'string') return false;
  if (typeof bic !== 'string') return false;
  if (typeof accountOwnerName !== 'string') return false;

  // check optionals

  // the following unused declarations trigger an error if the interface changed but the guard is not up to date. DO NOT REMOVE
  const obj = { id, creditInstitute, iban, bic, accountOwnerName };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isValid: BankAccount = obj;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const noNewOptionalProps: Omit<Required<BankAccount>, keyof typeof obj> = {};

  return true;
}

// guard for BankTransferDetails
export function isBankTransferDetails(value: unknown): value is BankTransferDetails {
  if (!isPlainObject(value)) return false;

  // Required fields
  if (!value.hasOwnProperty('id')) return false;

  // deconstruct all required props
  const {
    id,
  } = value;

  // check type of required
  if (typeof id !== 'number') return false;

  // check optionals

  let reference: string | undefined | null;

  if (value.hasOwnProperty('reference')) {
    if (typeof value.reference !== 'string' && value.reference !== null && value.reference !== undefined) return false;
    reference = value.reference;
  }

  // the following unused declarations trigger an error if the interface changed but the guard is not up to date. DO NOT REMOVE
  const obj = { id, reference };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isValid: BankTransferDetails = obj;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const noNewOptionalProps: Omit<Required<BankTransferDetails>, keyof typeof obj> = {};

  return true;
}

// guard for logo
export function isLogo(value: unknown): value is Logo {
  if (!isPlainObject(value)) return false;

  // Required fields
  if (!value.hasOwnProperty('id')) return false;
  if (!value.hasOwnProperty('format')) return false;
  if (!value.hasOwnProperty('data')) return false;

  // deconstruct all required props
  const {
    id,
    format,
    data,
  } = value;

  // check type of required
  if (typeof id !== 'number') return false;
  if (typeof format !== 'string') return false;
  if (typeof data !== 'string') return false;

  // the following unused declarations trigger an error if the interface changed but the guard is not up to date. DO NOT REMOVE
  const obj = { id, format, data };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isValid: Logo = obj;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  /* tslint:disable:no-unused-variable */
  const noNewOptionalProps: Omit<Required<Logo>, keyof typeof obj> = {};

  return true;
}

